import EventChannel from "@esWebAPI/es/util/EventChannel";

export type RatingValue = "UP" | "DOWN" | undefined;
/**
 * Categorised reason a user can attach to a thumbs-down rating. Mirrors the
 * RatingReason enum on the Java side (com.espatial.es.model.ai.RatingReason).
 */
export type RatingReason =
    "OFFENSIVE_UNSAFE" | "IRRELEVANT" | "MADE_UP" | "FAILED_TO_ANSWER" | "PARTIALLY_INCORRECT" | "OTHER";

/** Short model equivalent of AIUserInteraction.java */
export type AIUserInteraction = {
    threadId: string;
    messageId: string;
    messageType: string;
    messageContent: string;
    rating: RatingValue;
    ratingReason: RatingReason|undefined;
    ratingFeedback: string|undefined;
}

export default class AIModule {
    private readonly eventChannel: EventChannel;
    private readonly rqBroker: any;
    private readonly resourceName: string;
    private readonly cache: any;

    constructor(eventChannel: any, rqBroker:any, resourceName = "AI", cache: any) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;
    }

    public addUserInteraction(threadId: string, user: string, geoMapId: number, messageId: string, messageType: string, messageContent: string,
                              modelName: string, inputTokens: number, outputTokens: number,
                              opts?: any): Promise<AIUserInteraction> {
        const data = {
            tenantId: es.tenantId,
            userName: user,
            geoMapId: geoMapId,
            threadId: threadId,
            messageId: messageId,
            messageType: messageType,
            messageContent: messageContent,
            modelName: modelName,
            inputTokens: inputTokens,
            outputTokens: outputTokens,
        }

        return this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName) + '/userInteraction',
            data: data,
            opts: opts
        });
    }

    public setRating(threadId: string, messageId: string, rating: RatingValue,
                     reason?: RatingReason | null, feedback?: string | null, opts?: any): Promise<AIUserInteraction> {
        const data = {
            rating,
            ratingReason: reason ?? undefined,
            ratingFeedback: feedback ?? undefined
        }
        return this.rqBroker.doPut({
            url: es.api.getURL(this.resourceName) + `/userInteraction/${threadId}/${messageId}`,
            data: data,
            opts: opts
        });
    }

    /**
     * Persist standalone, thread-level feedback (not tied to rating a specific answer). Reuses the
     * userInteraction create endpoint: messageType "feedback", the prompt the user answered goes in
     * messageContent and their text in ratingFeedback, so it sits alongside chat interactions and is
     * queryable the same way. Used by the "Send Feedback" menu and any future custom-prompt entry point.
     */
    public submitGeneralFeedback(threadId: string, user: string, geoMapId: number, messageId: string,
                                 prompt: string, feedback: string, opts?: any): any {
        const data = {
            tenantId: es.tenantId,
            userName: user,
            geoMapId: geoMapId,
            threadId: threadId,
            messageId: messageId,
            messageType: "feedback",
            messageContent: prompt,
            ratingFeedback: feedback,
            modelName: null,
            inputTokens: 0,
            outputTokens: 0,
        }

        return this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName) + '/userInteraction',
            data: data,
            opts: opts
        });
    }

    public getByThreadId(threadId: string, opts?: any): Promise<AIUserInteraction[]> {
        return this.rqBroker.doGet({
            url: es.api.getURL(this.resourceName) + '/userInteraction/thread/' + threadId,
            opts: opts
        });
    }
}